import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { Alert, Badge, Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap"
import { Box } from "@mui/material"
import { withTranslation } from "react-i18next"
import classnames from "classnames"
import Loader from "react-loader-spinner"
import BaseDataTable from "../../components/Common/base-data-table"
import { apiBuscarConsultaVendaDetalhe } from "../../helpers/backend_helper"
import { describeDateTime, describeMoney } from "../../helpers/utils"
import { tableTokens } from "../../components/Common/table-theme"
import { montarHierarquiaItens, NIVEL_COMPONENTE } from "./consulta-vendas-itens"

const ABA_RESUMO = "resumo"
const ABA_PRODUTOS = "produtos"
const ABA_PAGAMENTOS = "pagamentos"
const ABA_FISCAL = "fiscal"

const STATUS_CANCELADA = "C"

const SEM_VALOR = "—"

const numero = valor => Number(valor || 0)

/* Quantidade do SMD é fracionária: as casas gravadas são preservadas, sem forçar decimais. */
const describeQuantidade = valor =>
  numero(valor).toLocaleString("pt-br", { minimumFractionDigits: 0, maximumFractionDigits: 4 })

const describePercentual = valor =>
  `${numero(valor).toLocaleString("pt-br", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`

const describeTexto = valor => {
  const texto = valor === null || valor === undefined ? "" : String(valor).trim()
  return texto || SEM_VALOR
}

/* Flags do SMD são gravadas como S/N; qualquer outro conteúdo é exibido como veio. */
const describeSimNao = (valor, t) => {
  const flag = String(valor || "").trim().toUpperCase()
  if (flag === "S") {
    return t("consultavendas.detalhe.sim")
  }
  if (flag === "N") {
    return t("consultavendas.detalhe.nao")
  }
  return describeTexto(valor)
}

/* Funcionário é gravado por código; o nome só existe quando o cadastro está sincronizado. */
const describeFuncionario = (nome, codigo) => {
  if (!numero(codigo)) {
    return SEM_VALOR
  }
  return nome ? `${nome} (${codigo})` : String(codigo)
}

/* Venda inexistente e empresa não autorizada são respostas previstas, não falha de comunicação. */
const descreveFalha = (falha, t) => {
  switch (falha?.response?.status) {
  case 404:
    return t("consultavendas.detalhe.naoencontrada")
  case 403:
    return t("consultavendas.detalhe.semacesso")
  default:
    return t("consultavendas.detalhe.erro")
  }
}

const celulaDireita = conteudo => (
  <Box sx={ { display: "block", textAlign: "right" } }>{ conteudo }</Box>
)

const Campo = ({ label, value }) => (
  <Col md="4" sm="6" className="mb-3">
    <div className="text-muted font-size-12">{ label }</div>
    <div className="fw-medium">{ value }</div>
  </Col>
)

Campo.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node
}

/* Valores zerados não são formas utilizadas na venda e por isso não entram na lista. */
const ListaDeValores = ({ titulo, valores }) => {
  const visiveis = valores.filter(({ value, sempre }) => sempre || numero(value) !== 0)

  if (visiveis.length === 0) {
    return null
  }

  return (
    <React.Fragment>
      <h5 className="font-size-14 mb-3">{ titulo }</h5>
      <Row className="mb-2">
        { visiveis.map(({ label, value, formato }) => (
          <Campo key={ label }
                 label={ label }
                 value={ formato === "numero" ? describeQuantidade(value) : describeMoney(value) } />
        )) }
      </Row>
    </React.Fragment>
  )
}

ListaDeValores.propTypes = {
  titulo: PropTypes.string,
  valores: PropTypes.array
}

/**
 * Detalhamento somente leitura da venda selecionada no grid. Busca cabeçalho e itens em uma única
 * chamada assim que abre; a lista por trás continua montada, então os filtros do grid permanecem.
 */
const ConsultaVendasDetalheModal = props => {
  const { isOpen, venda: vendaSelecionada, onClose, t } = props

  const [aba, setAba] = useState(ABA_RESUMO)
  const [detalhe, setDetalhe] = useState(null)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState(null)

  const idEmpresa = vendaSelecionada?.idEmpresa
  const seq = vendaSelecionada?.Seq

  /*
   * A resposta de uma venda já fechada é descartada: sem isso, abrir A, fechar e abrir B faria a
   * resposta atrasada de A sobrescrever a de B, exibindo os dados de uma venda sob o título da outra.
   */
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    let consultaAtual = true

    setAba(ABA_RESUMO)
    setDetalhe(null)

    /*
     * Sem empresa ou sequência não há o que consultar. O carregamento precisa terminar assim
     * mesmo: quem só sai do estado de busca pela resposta deixaria o modal girando para sempre.
     */
    if (!idEmpresa || !seq) {
      setErro(t("consultavendas.detalhe.naoencontrada"))
      setLoading(false)
      return undefined
    }

    setErro(null)
    setLoading(true)

    apiBuscarConsultaVendaDetalhe({ idEmpresa, seq })
      .then(resposta => {
        if (consultaAtual) setDetalhe(resposta)
      })
      .catch(falha => {
        if (consultaAtual) setErro(descreveFalha(falha, t))
      })
      .finally(() => {
        if (consultaAtual) setLoading(false)
      })

    return () => {
      consultaAtual = false
    }
  }, [isOpen, idEmpresa, seq])

  const venda = detalhe?.venda
  const itens = useMemo(() => montarHierarquiaItens(detalhe?.itens || []), [detalhe])

  /*
   * setLoading(true) só roda no efeito, depois do primeiro render com o modal aberto. Enquanto não
   * há resposta nem erro a tela ainda está buscando — sem isso o alerta de venda ausente pisca.
   */
  const carregando = loading || (!detalhe && !erro)

  const cancelada = venda?.Status === STATUS_CANCELADA
  const possuiNota = venda && venda.nfe_status !== null && venda.nfe_status !== undefined

  const valoresDaVenda = venda ? [
    { label: t("consultavendas.detalhe.valores.produtos"), value: venda.total_venda, sempre: true },
    { label: t("consultavendas.detalhe.valores.desconto"), value: venda.Desconto },
    { label: t("consultavendas.detalhe.valores.promocao"), value: venda.Promocao },
    { label: t("consultavendas.detalhe.valores.acrescimo"), value: venda.Acrescimo },
    { label: t("consultavendas.detalhe.valores.arredondamento"), value: venda.Arredondamento },
    { label: t("consultavendas.detalhe.valores.taxaentrega"), value: venda.Taxa_Entrega },
    { label: t("consultavendas.detalhe.valores.reembolso"), value: venda.FP_Reembolso },
    { label: t("consultavendas.detalhe.valores.valetroca"), value: venda.vale_troca },
    { label: t("consultavendas.detalhe.valores.devolucao"), value: venda.valor_devolucao },
    { label: t("consultavendas.detalhe.valores.totalrecebido"), value: venda.total_recebido, sempre: true }
  ] : []

  const formasDePagamento = venda ? [
    { label: t("consultavendas.detalhe.pagamento.dinheiro"), value: venda.Dinheiro },
    { label: t("consultavendas.detalhe.pagamento.troco"), value: venda.Troco },
    { label: t("consultavendas.detalhe.pagamento.dinheirorecebido"), value: venda.dinheiro_recebido },
    { label: t("consultavendas.detalhe.pagamento.cartao"), value: venda.Cartao },
    { label: t("consultavendas.detalhe.pagamento.cartaoparcelas"), value: venda.Cartao_Parcelas, formato: "numero" },
    { label: t("consultavendas.detalhe.pagamento.cheque"), value: venda.Cheque },
    { label: t("consultavendas.detalhe.pagamento.chequeparcelas"), value: venda.Cheque_Parcelas, formato: "numero" },
    { label: t("consultavendas.detalhe.pagamento.prazo"), value: venda.Prazo },
    { label: t("consultavendas.detalhe.pagamento.entrada"), value: venda.Valor_Entrada }
  ] : []

  /*
   * Colunas dos itens. Onde a célula mostra texto traduzido (tipo do item, controlado), o valor é
   * resolvido por accessorFn: assim o filtro da coluna procura pelo que está na tela, e não pela
   * flag crua gravada no banco.
   */
  const colunasProdutos = useMemo(() => [
    { header: t("consultavendas.detalhe.produtos.sequencia"), accessorKey: "Seq", size: 100 },
    {
      header: t("consultavendas.detalhe.produtos.tipo"),
      id: "tipo_item",
      accessorFn: item => item.nivel === NIVEL_COMPONENTE
        ? t("consultavendas.detalhe.produtos.tipo.componente")
        : t("consultavendas.detalhe.produtos.tipo.principal"),
      size: 140,
      Cell: ({ cell, row }) => (
        <Badge className={ row.original.nivel === NIVEL_COMPONENTE ? "badge-soft-info" : "badge-soft-secondary" } pill>
          { cell.getValue() }
        </Badge>
      )
    },
    { header: t("consultavendas.detalhe.produtos.codigo"), accessorKey: "Codigo", size: 110 },
    {
      header: t("consultavendas.detalhe.produtos.descricao"),
      accessorKey: "Descricao",
      size: 280,
      Cell: ({ cell, row }) => (
        <Box sx={ row.original.nivel === NIVEL_COMPONENTE ? { paddingLeft: "1.5rem" } : undefined }>
          { describeTexto(cell.getValue()) }
        </Box>
      )
    },
    { header: t("consultavendas.detalhe.produtos.descricaocomplementar"), accessorKey: "Descricao2", size: 220,
      Cell: ({ cell }) => describeTexto(cell.getValue()) },
    { header: t("consultavendas.detalhe.produtos.unidade"), accessorKey: "Unidade", size: 110,
      Cell: ({ cell }) => describeTexto(cell.getValue()) },
    { header: t("consultavendas.detalhe.produtos.codbarra"), accessorKey: "Cod_Barra", size: 160,
      Cell: ({ cell }) => describeTexto(cell.getValue()) },
    { header: t("consultavendas.detalhe.produtos.lote"), accessorKey: "Lote", size: 120,
      Cell: ({ cell }) => describeTexto(cell.getValue()) },
    { header: t("consultavendas.detalhe.produtos.controlado"), id: "Controlado", size: 130,
      accessorFn: item => describeSimNao(item.Controlado, t) },
    { header: t("consultavendas.detalhe.produtos.situacao"), accessorKey: "Status", size: 120,
      Cell: ({ cell }) => describeTexto(cell.getValue()) },
    { header: t("consultavendas.detalhe.produtos.quantidade"), accessorKey: "Qtd", size: 110,
      Cell: ({ cell }) => celulaDireita(describeQuantidade(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.precooriginal"), accessorKey: "PVenda", size: 140,
      Cell: ({ cell }) => celulaDireita(describeMoney(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.precopromocional"), accessorKey: "Promocao", size: 160,
      Cell: ({ cell }) => celulaDireita(describeMoney(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.precoaplicado"), accessorKey: "preco_aplicado", size: 140,
      Cell: ({ cell }) => celulaDireita(describeMoney(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.descontopercentual"), accessorKey: "Desconto", size: 140,
      Cell: ({ cell }) => celulaDireita(describePercentual(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.descontovalor"), accessorKey: "Desconto_Vlr", size: 140,
      Cell: ({ cell }) => celulaDireita(describeMoney(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.subtotal"), accessorKey: "subtotal_item", size: 140,
      Cell: ({ cell }) => celulaDireita(describeMoney(cell.getValue())) },
    { header: t("consultavendas.detalhe.produtos.subtotalconsiderado"), accessorKey: "subtotal_considerado", size: 180,
      Cell: ({ cell }) => celulaDireita(describeMoney(cell.getValue())) }
  ], [t])

  const abas = [
    { id: ABA_RESUMO, label: t("consultavendas.detalhe.aba.resumo") },
    { id: ABA_PRODUTOS, label: t("consultavendas.detalhe.aba.produtos") },
    { id: ABA_PAGAMENTOS, label: t("consultavendas.detalhe.aba.pagamentos") },
    { id: ABA_FISCAL, label: t("consultavendas.detalhe.aba.fiscal") }
  ]

  const renderCarregando = () => (
    <div className="text-center p-4">
      <Loader type="ThreeDots" color="#556EE6" height={ 60 } width={ 60 } />
    </div>
  )

  const renderResumo = () => (
    <Row>
      <Campo label={ t("consultavendas.detalhe.resumo.empresa") } value={ describeTexto(venda.nomeEmpresa) } />
      <Campo label={ t("consultavendas.detalhe.resumo.loja") } value={ describeTexto(venda.Loja) } />
      <Campo label={ t("consultavendas.detalhe.resumo.sequencia") } value={ describeTexto(venda.Seq) } />
      <Campo label={ t("consultavendas.detalhe.resumo.data") } value={ describeDateTime(venda.Data) } />
      <Campo label={ t("consultavendas.detalhe.resumo.situacao") } value={ describeTexto(venda.status_descricao) } />
      <Campo label={ t("consultavendas.detalhe.resumo.tipo") } value={ describeTexto(venda.tipo_descricao) } />
      <Campo label={ t("consultavendas.detalhe.resumo.canal") } value={ describeTexto(venda.canal_descricao) } />
      <Campo label={ t("consultavendas.detalhe.resumo.caixa") } value={ describeTexto(venda.Caixa) } />
      <Campo label={ t("consultavendas.detalhe.resumo.seqcaixa") } value={ describeTexto(venda.Seq_Caixa) } />
      <Campo label={ t("consultavendas.detalhe.resumo.operador") }
             value={ describeFuncionario(venda.operador_caixa_nome, venda.Func_Caixa) } />
      <Campo label={ t("consultavendas.detalhe.resumo.vendedor") }
             value={ describeFuncionario(venda.vendedor_nome, venda.Func_Venda) } />
      <Campo label={ t("consultavendas.detalhe.resumo.origem") } value={ describeTexto(venda.Origem_Venda) } />
      <Campo label={ t("consultavendas.detalhe.resumo.pedido") }
             value={ numero(venda.ID_PEDIDO) ? String(venda.ID_PEDIDO) : SEM_VALOR } />
      <Campo label={ t("consultavendas.detalhe.resumo.consumidor") } value={ describeTexto(venda.consumidor_nome) } />
      <Campo label={ t("consultavendas.detalhe.resumo.documento") } value={ describeTexto(venda.consumidor_documento) } />
      <Campo label={ t("consultavendas.detalhe.resumo.itensapresentados") }
             value={ describeQuantidade(venda.quantidade_itens_apresentados) } />
      <Campo label={ t("consultavendas.detalhe.resumo.itensprincipais") }
             value={ describeQuantidade(venda.quantidade_itens_principais) } />
      <Campo label={ t("consultavendas.detalhe.resumo.totalvenda") } value={ describeMoney(venda.total_venda) } />
      <Campo label={ t("consultavendas.detalhe.resumo.totalrecebido") } value={ describeMoney(venda.total_recebido) } />
    </Row>
  )

  const renderProdutos = () => {
    if (itens.length === 0) {
      return (<Alert color="info" className="mb-0">{ t("consultavendas.detalhe.produtos.vazio") }</Alert>)
    }

    return (
      <React.Fragment>
        <BaseDataTable
          columns={ colunasProdutos }
          data={ itens }
          enablePagination={ false }
          enableBottomToolbar={ false }
          enableStickyHeader
          muiTableContainerProps={ { sx: { maxHeight: "50vh" } } }
          muiTableBodyRowProps={ ({ row, staticRowIndex }) => ({
            sx: {
              backgroundColor: row.original.nivel === NIVEL_COMPONENTE
                ? "var(--bs-gray-200)"
                : (staticRowIndex % 2 === 0 ? tableTokens.stripeBg : undefined)
            }
          }) }
        />
        <small className="text-muted d-block mt-2">{ t("consultavendas.detalhe.produtos.ajuda") }</small>
      </React.Fragment>
    )
  }

  const renderPagamentos = () => (
    <React.Fragment>
      <ListaDeValores titulo={ t("consultavendas.detalhe.valores.titulo") } valores={ valoresDaVenda } />
      <ListaDeValores titulo={ t("consultavendas.detalhe.pagamento.titulo") } valores={ formasDePagamento } />
    </React.Fragment>
  )

  const renderFiscal = () => {
    if (!possuiNota) {
      return (<Alert color="info" className="mb-0">{ t("consultavendas.detalhe.fiscal.semnota") }</Alert>)
    }

    return (
      <Row>
        <Campo label={ t("consultavendas.detalhe.fiscal.numero") } value={ describeTexto(venda.nfe_numero) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.serie") } value={ describeTexto(venda.nfe_serie) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.modelo") } value={ describeTexto(venda.nfe_modelo) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.emissao") } value={ describeDateTime(venda.nfe_data_emissao) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.chave") } value={ describeTexto(venda.nfe_chave) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.situacao") } value={ describeTexto(venda.nfe_status_descricao) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.situacaodata") } value={ describeDateTime(venda.nfe_status_data) } />
        <Campo label={ t("consultavendas.detalhe.fiscal.tipoemissao") }
               value={ describeTexto(venda.nfe_tipo_emissao_descricao) } />
        { venda.nfe_cancelamento_protocolo &&
          <Campo label={ t("consultavendas.detalhe.fiscal.protocolocancelamento") }
                 value={ describeTexto(venda.nfe_cancelamento_protocolo) } /> }
      </Row>
    )
  }

  const renderConteudo = () => (
    <React.Fragment>
      <Nav tabs>
        { abas.map(({ id, label }) => (
          <NavItem key={ id }>
            <NavLink className={ classnames({ active: aba === id }) }
                     onClick={ () => setAba(id) }
                     style={ { cursor: "pointer" } }>
              { label }
            </NavLink>
          </NavItem>
        )) }
      </Nav>
      <TabContent activeTab={ aba } className="p-3">
        <TabPane tabId={ ABA_RESUMO }>{ aba === ABA_RESUMO && renderResumo() }</TabPane>
        <TabPane tabId={ ABA_PRODUTOS }>{ aba === ABA_PRODUTOS && renderProdutos() }</TabPane>
        <TabPane tabId={ ABA_PAGAMENTOS }>{ aba === ABA_PAGAMENTOS && renderPagamentos() }</TabPane>
        <TabPane tabId={ ABA_FISCAL }>{ aba === ABA_FISCAL && renderFiscal() }</TabPane>
      </TabContent>
    </React.Fragment>
  )

  return (
    <Modal isOpen={ isOpen } toggle={ onClose } size="xl" centered scrollable>
      <ModalHeader toggle={ onClose }>
        { t("consultavendas.detalhe.titulo", { seq: seq || "" }) }
        { cancelada &&
          <Badge className="badge-soft-danger ms-2" color="danger" pill>
            { t("consultavendas.detalhe.cancelada") }
          </Badge> }
      </ModalHeader>
      <ModalBody>
        { carregando && renderCarregando() }
        { !carregando && erro && <Alert color="danger" className="mb-0">{ erro }</Alert> }
        { !carregando && !erro && !venda &&
          <Alert color="info" className="mb-0">{ t("consultavendas.detalhe.naoencontrada") }</Alert> }
        { !carregando && !erro && venda && renderConteudo() }
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={ onClose }>{ t("consultavendas.detalhe.fechar") }</Button>
      </ModalFooter>
    </Modal>
  )
}

ConsultaVendasDetalheModal.propTypes = {
  isOpen: PropTypes.bool,
  venda: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func
}

export default withTranslation()(ConsultaVendasDetalheModal)
